import { getLocalStorageItem, setLocalStorageItem } from '@snapchat/mw-common';
import noop from 'lodash-es/noop';
import { useCallback, useContext, useEffect, useState } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logEvent, SubscribedEventType } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import type { EcommerceEventProduct } from '../../types/gtm';
import { ShopifyContext } from '../ShopifyProvider';
import type { BasicCart, CartEvent, CartState } from './types';

const shopifyCartKey = 'mwp-shopify-cart';

export const initialCartState: CartState = {
  lineItems: {},
  cartOpen: false,
  addOne: noop,
  removeOne: noop,
  setCartOpen: noop,
  discountCode: '',
  setDiscountCode: noop,
  sellingPlanSelections: {},
  setSellingPlan: noop,
};

// TODO: Discount code flow is for the waitlist purchase flow (months away).
// This must remain purely client-side to avoid CDN cache interference.
// Needs validation logic added later — currently we pass the code to Shopify
// checkout without any client-side validation.
const getUrlDiscount = (urlString: string) => {
  try {
    const url = new URL(urlString);
    return url.searchParams.get('discount') ?? '';
  } catch {
    return '';
  }
};

export const useCart = (): CartState => {
  const appContext = useContext(AppContext);
  const { elementLocation } = useContext(ElementLocation);
  const { catalog, productOptionById } = useContext(ShopifyContext);
  const country = appContext.userLocation.country;

  // Seed discount code from URL query param (e.g. ?discount=SAVE10)
  const urlDiscount = getUrlDiscount(appContext.getCurrentUrl());

  const [cart, setCart] = useState<CartState>(initialCartState);
  const [discountCode, setDiscountCode] = useState(urlDiscount);
  const [sellingPlanSelections, setSellingPlanSelections] = useState<Record<string, string>>({});

  const setSellingPlan = useCallback((optionId: string, sellingPlanId: string) => {
    setSellingPlanSelections(prev => ({ ...prev, [optionId]: sellingPlanId }));
  }, []);

  const setCartOpen = (cartOpen: boolean) => {
    if (cartOpen) {
      logEvent({
        eventCategory: 'Cart',
        eventAction: UserAction.Click,
        eventLabel: 'Open Cart',
        subscribedEventType: SubscribedEventType.USER_INTERACTION,
      });
    } else {
      logEvent({
        eventCategory: 'Cart',
        eventAction: UserAction.Click,
        eventLabel: 'Close Cart',
        subscribedEventType: SubscribedEventType.USER_INTERACTION,
      });
    }

    setCart({ ...cart, cartOpen });
  };

  const addOne = useCallback(
    ({ productKey, optionId, currencyCode }: CartEvent) => {
      const newLineItems = { ...cart.lineItems };
      const product = catalog[productKey];
      const option = productOptionById[optionId]?.option;

      if (!product) return;

      // TODO: Consider a more stable cart key strategy (e.g. composite productKey+optionId).
      const productInCart = newLineItems[optionId];

      newLineItems[optionId] = (productInCart ?? 0) + 1;

      const ecommerceEventProducts: EcommerceEventProduct[] = [
        {
          name: product.title,
          id: option?.sku ?? product.options[0]?.sku ?? '',
          variant: option?.title ?? product.title,
          price: (option?.price ?? product.options[0]?.price ?? 0).toString(),
          shopifyid: optionId,
          quantity: 1,
        },
      ];

      // TODO Phase 2: Bundle expansion deferred. See TDD for cart line restructure.
      // Bundle/accessory auto-add was removed in Phase 1 — it was broken (variant ID vs
      // handle key mismatch) and Stinson has no bundles at Launch.

      setLocalStorageItem(shopifyCartKey, JSON.stringify({ lineItems: newLineItems }));

      logEvent({
        eventCategory: 'Cart',
        eventAction: 'Add',
        eventLabel: ecommerceEventProducts.map(ecomProduct => ecomProduct.shopifyid).join(','),
        numberOfProducts: ecommerceEventProducts.length,
        ecommerce: {
          currencyCode,
          add: {
            products: ecommerceEventProducts,
          },
        },
        subscribedEventType: SubscribedEventType.ECOMMERCE,
        context: { elementLocation },
      });

      setCart({
        ...cart,
        lineItems: newLineItems,
        cartOpen: true,
      });
    },
    [cart, elementLocation, catalog, productOptionById]
  );

  const removeOne = useCallback(
    ({ productKey, optionId, currencyCode }: CartEvent) => {
      const newLineItems = { ...cart.lineItems };
      const product = catalog[productKey];
      const option = productOptionById[optionId]?.option;

      if (!product) return;

      const productInCart = newLineItems[optionId] ?? 0;

      if (productInCart > 0) {
        const newQty = productInCart - 1;

        if (newQty === 0) {
          delete newLineItems[optionId];
        } else {
          newLineItems[optionId] = newQty;
        }
      }

      const ecommerceEventProducts: EcommerceEventProduct[] = [
        {
          name: product.title,
          id: option?.sku ?? product.options[0]?.sku ?? '',
          variant: option?.title ?? product.title,
          price: (option?.price ?? product.options[0]?.price ?? 0).toString(),
          shopifyid: optionId,
          quantity: 1,
        },
      ];

      // TODO Phase 2: Bundle expansion deferred. See TDD for cart line restructure.

      setLocalStorageItem(shopifyCartKey, JSON.stringify({ lineItems: newLineItems }));

      logEvent({
        eventCategory: 'Cart',
        eventAction: 'Remove',
        eventLabel: ecommerceEventProducts.map(ecomProduct => ecomProduct.shopifyid).join(','),
        currencyCode,
        productIds: ecommerceEventProducts.map(ecomProduct => ecomProduct.shopifyid).join(','),
        ecommerce: {
          remove: {
            products: ecommerceEventProducts,
          },
        },
        subscribedEventType: SubscribedEventType.ECOMMERCE,
        context: { elementLocation },
      });

      setCart({
        ...cart,
        lineItems: newLineItems,
      });
    },
    [cart, elementLocation, catalog, productOptionById]
  );

  const cartInit = useCallback(() => {
    const initialCartString: string | BasicCart | null = getLocalStorageItem(shopifyCartKey);
    let initialCart: BasicCart;

    if (initialCartString) {
      initialCart = JSON.parse(initialCartString) as BasicCart;
    } else {
      initialCart = {
        lineItems: {},
      } as BasicCart;

      setLocalStorageItem(shopifyCartKey, JSON.stringify(initialCart));
    }

    setCart(currentCart => ({
      ...currentCart,
      ...(initialCart as BasicCart),
      cartOpen: false,
    }));
  }, []);

  useEffect(() => {
    if (!Config.shopify) {
      return;
    }

    cartInit();
  }, [country, cartInit]);

  return {
    ...cart,
    addOne,
    removeOne,
    setCartOpen,
    discountCode,
    setDiscountCode,
    sellingPlanSelections,
    setSellingPlan,
  };
};
